﻿import { useMemo } from "react";
import { useNavigate } from "react-router-dom";
import Badge from "@/components/base/Badge";
import type { CustomerListItem } from "@/lib/api";

const estadoVariant: Record<string, "success" | "error" | "warning" | "neutral"> = {
  activo: "success",
  moroso: "warning",
  cortado: "error",
  inactivo: "neutral",
};

const estadoLabel: Record<string, string> = {
  activo: "Activo",
  moroso: "Moroso",
  cortado: "Cortado",
  inactivo: "Inactivo",
};

type Props = {
  rows: CustomerListItem[];
  isLoading: boolean;
  error: string | null;
};

export default function ClienteTable({ rows, isLoading, error }: Props) {
  const navigate = useNavigate();
  const filtered = useMemo(() => rows.slice(0, 100), [rows]);

  return (
    <div className="bg-white rounded-xl border border-gray-100 overflow-hidden">
      {error && (
        <div className="px-4 py-3 border-b border-amber-200 bg-amber-50 text-amber-800 text-sm">{error}</div>
      )}

      <div className="overflow-x-auto">
        <table className="w-full">
          <thead>
            <tr className="bg-gray-50 border-b border-gray-100">
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Cliente</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">RUT</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Contrato</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Tipo</th>
              <th className="text-left px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Estado</th>
              <th className="text-right px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Deuda</th>
              <th className="text-center px-4 py-3 text-xs font-semibold text-gray-500 uppercase tracking-wide">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                  Cargando clientes...
                </td>
              </tr>
            ) : filtered.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-4 py-8 text-center text-sm text-gray-500">
                  Busca un cliente para ver resultados.
                </td>
              </tr>
            ) : (
              filtered.map((cliente, i) => (
                <tr
                  key={cliente.id}
                  className={`border-b border-gray-50 hover:bg-blue-50/30 transition-colors cursor-pointer ${i % 2 === 0 ? "" : "bg-gray-50/30"}`}
                  onClick={() => navigate(`/clientes/${cliente.id}`)}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 flex items-center justify-center rounded-full bg-blue-100 text-blue-700 text-xs font-bold flex-shrink-0">
                        {cliente.nombre.split(" ").map((n) => n[0]).slice(0, 2).join("")}
                      </div>
                      <div>
                        <div className="text-sm font-medium text-gray-800">{cliente.nombre}</div>
                        <div className="text-xs text-gray-400">{cliente.email}</div>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600">{cliente.rut}</td>
                  <td className="px-4 py-3 text-sm text-gray-600">
                    {cliente.contracts.length > 0 ? (
                      <div className="flex flex-wrap gap-1">
                        {cliente.contracts.slice(0, 2).map((contract) => (
                          <span key={contract.id || contract.contractNumber} className="rounded-full bg-cyan-50 px-2 py-0.5 text-xs font-medium text-cyan-700">
                            {contract.contractNumber}
                          </span>
                        ))}
                        {cliente.contracts.length > 2 ? (
                          <span className="rounded-full bg-gray-100 px-2 py-0.5 text-xs text-gray-500">+{cliente.contracts.length - 2}</span>
                        ) : null}
                      </div>
                    ) : (
                      <span className="text-gray-400">-</span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-600 capitalize">{cliente.customerType}</td>
                  <td className="px-4 py-3">
                    <Badge variant={estadoVariant[cliente.estado]} size="sm">
                      {estadoLabel[cliente.estado]}
                    </Badge>
                  </td>
                  <td className="px-4 py-3 text-right">
                    {cliente.deuda > 0 ? (
                      <span className="text-sm font-semibold text-red-600">${cliente.deuda.toLocaleString("es-CL")}</span>
                    ) : (
                      <span className="text-sm text-emerald-600 font-medium">Al dia</span>
                    )}
                  </td>
                  <td className="px-4 py-3">
                    <div className="flex items-center justify-center gap-1" onClick={(e) => e.stopPropagation()}>
                      <button
                        onClick={() => navigate(`/clientes/${cliente.id}`)}
                        className="w-7 h-7 flex items-center justify-center rounded-lg hover:bg-blue-100 text-gray-400 hover:text-blue-600 transition-colors cursor-pointer"
                        title="Ver ficha"
                      >
                        <i className="ri-eye-line text-sm" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      <div className="px-4 py-3 border-t border-gray-100 flex items-center justify-between">
        <span className="text-xs text-gray-400">Mostrando {filtered.length} cliente(s)</span>
      </div>
    </div>
  );
}
